import { DispatchCard, UnassignedItem } from 'src/entities/dispatch';
import { DeliveryListGroupingValues } from 'src/entities/licenseEntity';

import { VehicleRowGroup } from './adapter';
import { PlaceSide, placeKeyOf, placeKeysOf } from './placeKeys';

/**
 * その日のデータから拠点 (配送センター・共同物流倉庫) の地点キーを推定する
 * (logposeTech/log_web_issues#318 の拡張)。
 *
 * Why: 同一地点タグの判定に拠点が入るとノイズになるので `places.is_main_warehouse` で
 * 除外しているが、フラグを設定していない会社では効かない。ほとんどの会社は 1〜2 箇所の
 * 配送センターから多数の地点へ運ぶ構造なので、地点ごとの登場割合に拠点と一般顧客の谷が出る。
 *
 * 実測 (dev / company 1 / 2026-08-08 / legRun 210 件 / 地点 127 種):
 *
 * | 順位 | 割合 | 件数 | 地点 |
 * | --- | --- | --- | --- |
 * | 1 | 87.14% | 183 | 富士共同物流 (塩浜) |
 * | 2 | 10.00% | 21 | 富士共同物流 (浦和) |
 * | 3 | 4.29% | 9 | 世田谷区 保健福祉課 |
 * | 4〜8 | 3.33〜2.38% | 7〜5 | 一般顧客 |
 *
 * 2 位と 3 位の間に 2.3 倍の谷がある。
 *
 * ただしこの谷は「1〜2 箇所のセンターから運ぶ会社」でしか出ない。本番 16 社の実測
 * (2026-08) では 103 DNPロジスティクス (1 位 44.2%) と 214 合田運送 (1 位 34.8%) が
 * 拠点型でなく、割合だけで決めると 7〜11 箇所が拠点扱いになった。そこで
 * `HUB_GATE_MAX_PERCENT` のゲートを先に通す。
 *
 * Why not フラグを置き換える: 実測 8 位に 2.38% (5 件) の「富士共同物流」(第 3 の座標) が
 * ある。割合では拾えないがフラグなら拾える地点で、逆にフラグ未設定の会社は推定でしか
 * 拾えない。どちらか一方では取りこぼすので、呼び出し側で OR にする。
 */

/**
 * 拠点とみなす登場割合。実測の谷 (10.00% と 4.29% の間) の内側に置く。
 *
 * Why 整数の百分率で持つ: `count / total >= 0.07` は境界がまるめ誤差に乗る。
 * `count * 100 >= total * 7` なら整数どうしの比較で 7/100 がちょうど閾値に入る。
 *
 * この 7% は**座標だけでキーを作る設定 (`groupByName` 以外) を前提にした値**。
 * 名称をキーに含める設定では同一座標の別名称が別地点に割れ、拠点の割合がその数だけ
 * 下がるので、拾えなくなる方向にずれる (安全側。タグが増えるだけで消えはしない)。
 */
export const HUB_RATIO_PERCENT = 7;

/**
 * 推定を走らせる最小の母集団。案件が数件しか無い日は、1 件通っただけの地点が
 * 高い割合になり、一般顧客が拠点に化ける。
 */
export const HUB_MIN_POPULATION = 20;

/**
 * ゲート。その日の最大割合がこれ未満なら、その日は推定しない。
 *
 * Why: 7% は「1 位が突出している日」でしか意味を持たない。1 位が 30〜45% しかない
 * 巡回型の会社では 7% が一般顧客の帯に入り込み、本番実測 (2026-08) で 103 は
 * 中央値 7 箇所・最大 11 箇所、214 は同じく 7 箇所・11 箇所が拠点扱いになった。
 * ゲートを入れると 214 は全 18 日で推定オフ、103 は 23 日中 7 日だけになる。
 *
 * Why not 閾値 (7%) を上げて解決する: 本物の拠点が 5 箇所ある会社が実在する
 * (159 中央興産: ニチレイ東京 77.1% / 松岡東京Central DC 17.8% / 五十嵐冷蔵城南島
 * 15.9% / ロジスティクス・ネットワーク東京 10.7% / 横浜冷凍東京第2 7.0%)。閾値を
 * 20% まで上げるとこの会社の拠点を取りこぼす。誤りの向きが非対称で、**過剰除外は
 * タグが黙って消えて機能が死ぬ**のに対し、**拾い漏れはタグが増えるだけで、主要倉庫
 * フラグでも補える**。だから「割合の線を動かす」のではなく「拠点型でない日は
 * 推定しない」に倒す。159 は 1 位 77.1% でゲートを通るので 5 箇所とも残る。
 */
export const HUB_GATE_MAX_PERCENT = 50;

/**
 * 配車済みとして数えるカードか。
 *
 * 固定ルート由来の仮カードはまだ配車されていない。`no-delivery` は legRun を持たず、
 * `fr-template` が持つ legRun は「同名の未割当 legRun」の候補なので、母集団に足すと
 * 未割当側と同じ案件を別の地点キーで数えることになる。どちらも数えない。
 */
export const isDispatchedCard = (card: DispatchCard): boolean => !(
  card.type === 'delivery' && (card.status === 'no-delivery' || card.status === 'fr-template')
);

/** 数える側。積地・降地の両方で登場した地点を拾う */
const COUNTED_SIDES: readonly PlaceSide[] = ['loading', 'unloading'];

/**
 * 案件の同一性キー。legRun の ID で決める。
 *
 * Why 未割当とカードで同じ形にする: D&D 直後の楽観更新では、同じ legRun が未割当リストと
 * 配車済みカードの両方に一時的に載る。二重に数えると母集団が膨らみ、拠点の割合が薄まる。
 * `toUnassignedItem` の id も `lr-{legRun.id}` なので、ID を持たない案件
 * (`lr-idx-{index}` にフォールバックしたもの) はそのまま一意な値として扱える。
 */
const caseKeyOfItem = (item: UnassignedItem): string => (
  item.source?.id != null ? `lr-${item.source.id}` : item.id
);

/**
 * 拠点とみなす地点キーの集合を返す。
 *
 * 母集団はその日の全案件 = 未割当 `items` ＋ 配車済みカードの legRun。数えるのは
 * 「その地点が積地または降地として登場する案件数 ÷ 総案件数」で、1 案件の積地と降地が
 * 同じ地点なら 1 回だけ数える (往復するだけの地点が 2 倍の割合に化けるのを防ぐ)。
 *
 * 判定の順は 母集団ガード → ゲート (最大割合) → 閾値 (7%)。ゲートで弾いた日は
 * 空集合を返すので、拠点の除外は主要倉庫フラグだけが効く。
 *
 * キーは判定と同じ `placeKeyOf`。同じキーで数えないと、除外したい地点と数えた地点が
 * ずれる。
 */
export function estimateHubPlaceKeys(
  items: readonly UnassignedItem[],
  rowGroups: readonly VehicleRowGroup[],
  grouping: DeliveryListGroupingValues,
): ReadonlySet<string> {
  // 案件ごとの「触れた地点キー」。Set なので積地と降地が同じ地点なら 1 件に畳まれる
  const placeKeysByCase = new Map<string, Set<string>>();
  const add = (caseKey: string, placeKey: string): void => {
    const keys = placeKeysByCase.get(caseKey);
    if (keys) keys.add(placeKey);
    else placeKeysByCase.set(caseKey, new Set([placeKey]));
  };

  for (const item of items) {
    const caseKey = caseKeyOfItem(item);
    // fallback に item.id を渡すのは判定側 (collectTargetsForItem) と揃えるため。
    // 座標も名称も無い legRun は一意なキーになり、他の案件と同じ地点に括られない
    for (const side of COUNTED_SIDES) add(caseKey, placeKeyOf(side, item.source, item.id, grouping));
  }

  for (const group of rowGroups) {
    for (const row of group.rows) {
      for (const card of row.cards) {
        if (!isDispatchedCard(card) || !card.placeKey) continue;
        // 主要倉庫のカードも母集団に入れる。除外するのは判定の相手としてであって、
        // その案件がその日に存在した事実は変わらない (分母は案件単位なので影響しない)
        //
        // 代表座標キーへ寄せる前のキーも数える (log_web_issues#322)。判定側
        // (buildSameLocationAssignments) が別名キーでも索引するので、こちらが代表キー
        // だけを数えると別名側が拠点として外れず、拠点のタグが漏れて出る
        for (const legRunId of card.legRunIds ?? []) {
          for (const placeKey of placeKeysOf(card)) add(`lr-${legRunId}`, placeKey);
        }
      }
    }
  }

  const total = placeKeysByCase.size;
  if (total < HUB_MIN_POPULATION) return new Set();

  const countByPlaceKey = new Map<string, number>();
  for (const keys of placeKeysByCase.values()) {
    for (const key of keys) countByPlaceKey.set(key, (countByPlaceKey.get(key) ?? 0) + 1);
  }

  // 1 位が突出していない日は拠点型の運用ではないので、地点をひとつも外さない
  let maxCount = 0;
  for (const count of countByPlaceKey.values()) if (count > maxCount) maxCount = count;
  if (maxCount * 100 < total * HUB_GATE_MAX_PERCENT) return new Set();

  const hubs = new Set<string>();
  for (const [key, count] of countByPlaceKey) {
    if (count * 100 >= total * HUB_RATIO_PERCENT) hubs.add(key);
  }

  return hubs;
}
